import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ShoppingBag, Filter } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTS = ['alle', 'nieuw', 'in wachtrij', 'in uitvoering', 'wacht op goedkeuring', 'afgerond', 'mislukt'];
const TYPE_OPTS = ['alle', 'abonnement', 'optimalisatie', 'bundle', 'pakket', 'leads', 'website'];

const STATUS_STYLE = {
  nieuw: 'bg-orange-100 text-orange-700',
  'in wachtrij': 'bg-blue-100 text-blue-700',
  'in uitvoering': 'bg-purple-100 text-purple-700',
  afgerond: 'bg-green-100 text-green-700',
  mislukt: 'bg-red-100 text-red-700',
};

export default function OrdersPage() {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [typeFilter, setTypeFilter] = useState('alle');
  const [loading, setLoading] = useState(true);

  const load = () => {
    const q = new URLSearchParams();
    if (statusFilter !== 'alle') q.set('status', statusFilter);
    if (typeFilter !== 'alle') q.set('type_filter', typeFilter);
    api('get', `/api/admin/orders?${q}`).then(r => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const updateStatus = async (id, status) => {
    await api('post', `/api/admin/orders/${id}/status`, { status });
    toast.success(`Status bijgewerkt: ${status}`);
    load();
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-white">Bestellingen & Uitvoer</h1>
        <span className="text-[#64748b] text-sm">{orders.length} bestellingen</span>
      </div>

      {/* Filters */}
      <div className="bg-[#0f172a] rounded-2xl border border-white/10 p-4 mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#64748b]" />
          <span className="text-[#94a3b8] text-xs">Status:</span>
          {STATUS_OPTS.slice(0, 4).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${statusFilter === s ? 'bg-[#D4A017] text-[#0f172a]' : 'bg-white/10 text-[#94a3b8] hover:bg-white/20'}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#94a3b8] text-xs">Type:</span>
          {TYPE_OPTS.slice(0, 5).map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${typeFilter === t ? 'bg-[#D4A017] text-[#0f172a]' : 'bg-white/10 text-[#94a3b8] hover:bg-white/20'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Orders tabel */}
      <div className="bg-[#0f172a] rounded-2xl border border-white/10 overflow-hidden">
        <div className="grid grid-cols-6 text-[10px] font-bold text-[#475569] uppercase px-5 py-3 border-b border-white/5">
          <span className="col-span-2">Product / Klant</span><span>Prijs</span><span>Type</span><span>Status</span><span>Acties</span>
        </div>
        {loading ? (
          <div className="py-12 text-center"><div className="w-6 h-6 border-2 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center">
            <ShoppingBag className="w-10 h-10 text-[#475569] mx-auto mb-3" />
            <p className="text-[#64748b]">Geen bestellingen gevonden</p>
          </div>
        ) : orders.map(o => (
          <div key={o.id} className="grid grid-cols-6 items-center px-5 py-3.5 border-b border-white/5 hover:bg-white/5 transition-all">
            <div className="col-span-2">
              <p className="text-white text-xs font-medium">{o.product_name}</p>
              <p className="text-[#475569] text-[10px]">{o.company_name}</p>
            </div>
            <span className="text-[#D4A017] font-bold text-xs">€{o.price}</span>
            <span className="text-[10px] bg-white/10 text-[#94a3b8] px-2 py-0.5 rounded-full w-fit">{o.type}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold w-fit ${STATUS_STYLE[o.status] || 'bg-white/10 text-white'}`}>{o.status}</span>
            <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
              className="text-[10px] bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-[#D4A017] cursor-pointer">
              {STATUS_OPTS.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
